import React, { useEffect } from "react";
import { useStartOAuthLoginMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import {
  INTEGRATION_PROVIDERS,
  OAUTH_LOGIN_PROVIDER_KEYS,
  providerInitials,
} from "@/util/integrationProviders";
import { Button } from "@/components/ui/button";

export const OAUTH_LOGIN_STATE_KEY = "oauth_login_state";
export const OAUTH_LOGIN_REDIRECT_KEY = "oauth_login_redirect";

interface OAuthLoginButtonsProps {
  invite?: string;
  memberInvite?: string;
  redirect?: string;
}

const LOGIN_PROVIDERS = OAUTH_LOGIN_PROVIDER_KEYS.map((key) =>
  INTEGRATION_PROVIDERS.find((provider) => provider.key === key),
).filter((provider): provider is (typeof INTEGRATION_PROVIDERS)[number] => Boolean(provider));

export default function OAuthLoginButtons({ invite, memberInvite, redirect }: OAuthLoginButtonsProps) {
  const [startOAuthLogin, startResult] = useStartOAuthLoginMutation();

  useEffect(() => {
    if (startResult.isSuccess && startResult.data) {
      sessionStorage.setItem(OAUTH_LOGIN_STATE_KEY, startResult.data.state);
      sessionStorage.setItem(OAUTH_LOGIN_REDIRECT_KEY, redirect ?? "");
      window.location.href = startResult.data.url;
    }
  }, [startResult, redirect]);

  const onSelect = (provider: string) => {
    startOAuthLogin({ provider, invite, memberInvite });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground">or continue with</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {LOGIN_PROVIDERS.map((provider) => (
          <Button
            key={provider.key}
            type="button"
            variant="outline"
            className="justify-start gap-2"
            disabled={startResult.isLoading}
            onClick={() => onSelect(provider.key)}
          >
            <span
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold text-white"
              style={{ backgroundColor: provider.accent }}
            >
              {providerInitials(provider.name)}
            </span>
            <span className="truncate">{provider.name}</span>
          </Button>
        ))}
      </div>

      {startResult.isError && (
        <p className="text-sm text-destructive">
          {apiErrorMessage(startResult.error, "Could not start sign-in. Please try again.")}
        </p>
      )}
    </div>
  );
}
